import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux, actions, recompose
import { connect } from 'react-redux';
import { fetchData } from 'reducers/dataTable';
import { openModal } from 'reducers/modals';
// components
import Link from 'react-router/lib/Link';
import SearchBox from 'components/SearchBox';
import { FormattedMessage } from 'react-intl';
import Panel from 'components/Panel';
import DataTable from 'components/DataTable/DataTable';
import NothingBox from 'components/NothingBox';
import { CustomColumn } from 'components/DataTable/Columns';
import StatusBadge from 'components/StatusBadge/StatusBadge';
import BinFormModal from './BinFormModal';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import MessageBox from 'components/MessageBox';
import DropdownButton from 'components/DropdownButton';
import EmptyStateActions from 'components/EmptyStateActions';
// endpoints
import { createFarmBin, updateFarmBin, deleteFarmBin, getFasStatus } from 'endpoints/farms';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatBackEndWeightToSystem } from 'utils/weightHelper';
import { formatNumber, showToastrMessage } from 'utils';
// hooks
import useDataTableController from 'hooks/useDataTableController';
// assets
import './FarmBins.scss';

const labels = {
  dataTable: {
    name: <FormattedMessage id="component.dataTable.headers.binID" />,
    capacity: <FormattedMessage id="component.dataTable.headers.capacity" />,
    type: <FormattedMessage id="general.type" />,
    actions: <FormattedMessage id="general.actions" />,
  },
  bins: <FormattedMessage id="general.bins" />,
  addBin: <FormattedMessage id="general.addBin" />,
  editBin: <FormattedMessage id="general.editBin" />,
  deleteBin: <FormattedMessage id="general.deleteBin" />,
  fasIntegration: <FormattedMessage id="general.fasIntegration" />,
};

const FarmBins = ({
  fetchData,
  isLoading,
  meta,
  params = {},
  resources,
  reqParams,
  measurementSystem,
  openModal,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [fasStatus, setFasStatus] = useState({
    has_fas_integration: false,
    loading: true
  });

  const { onPageChange, onPerPageChange, onSearchChange, onSortChange } = useDataTableController(fetchData, params);
  const { search, sort, page, per_page } = reqParams;
  const farmId = params.id;

  const paginationProps = {
    totalItems: meta.total,
    currentPage: page,
    perPage: per_page,
    onPageChange,
    onPerPageChange,
  };

  const loadData = () => {
    fetchData(reqParams).catch(toastResponseErrors);
  };

  const loadFasStatus = async () => {
    try {
      const status = await getFasStatus(farmId);
      setFasStatus({
        ...status,
        loading: false
      });
    } catch (error) {
      setFasStatus({
        has_fas_integration: false,
        loading: false
      });
    }
  };

  // Load data and FAS status on mount
  useEffect(() => {
    loadData();
    loadFasStatus();
  }, []);

  // CRUD operations
  const handleCreateBin = async (binData) => {
    setIsUpdating(true);
    try {
      await createFarmBin(farmId, binData);
      showToastrMessage('component.toastr.bin.created');
      loadData();
    } catch (error) {
      throw error; // Let modal handle errors
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateBin = (bin) => async (binData) => {
    setIsUpdating(true);
    try {
      await updateFarmBin(farmId, bin.id, binData);
      showToastrMessage('component.toastr.bin.updated');
      loadData();
    } catch (error) {
      throw error; // Let modal handle errors
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteBin = async (bin) => {
    setIsUpdating(true);
    try {
      await deleteFarmBin(farmId, bin.id);
      showToastrMessage('component.toastr.bin.deleted');
      loadData();
    } catch (error) {
      toastResponseErrors(error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    openModal(
      <BinFormModal
        isOpen
        onClose={() => {}}
        onSubmit={handleCreateBin}
        bin={{}}
        isEditing={false}
        isLoading={isUpdating}
        measurementSystem={measurementSystem}
      />
    );
  };

  const openEditModal = (bin) => {
    openModal(
      <BinFormModal
        isOpen
        onClose={() => {}}
        onSubmit={handleUpdateBin(bin)}
        bin={bin}
        isEditing
        isLoading={isUpdating}
        measurementSystem={measurementSystem}
      />
    );
  };

  const openDeleteModal = (bin) => {
    openModal((
      <ConfirmationModal
        title={labels.deleteBin}
        actionBtnLabel={<FormattedMessage id="general.button.delete" />}
        actionBtnProps={{ red: true }}
        warningMessage={(
          <FormattedMessage
            id="component.confirmationModal.deleteBin"
            values={{ binName: bin?.name || 'Unknown' }}
          />
        )}
        handleConfirm={() => handleDeleteBin(bin)}
      />
    ));
  };

  // Column renderers
  const renderBinNameColumn = ({ name }) => (
    <CustomColumn flexRow>
      <span className="bin-name">{name}</span>
    </CustomColumn>
  );

  renderBinNameColumn.propTypes = {
    name: T.string
  };

  // VRPG-25: Updated capacity column to support measurement system
  const renderCapacityColumn = ({ capacity }) => {
    if (!capacity) return (
      <CustomColumn flexRow label={labels.dataTable.capacity}>
        -
      </CustomColumn>
    );

    const formattedCapacity = Math.round(formatBackEndWeightToSystem(capacity, measurementSystem));
    const unit = measurementSystem === 'metric' ? 'kg' : 'lbs';

    return (
      <CustomColumn flexRow label={labels.dataTable.capacity}>
        {formatNumber(formattedCapacity)} {unit}
      </CustomColumn>
    );
  };

  renderCapacityColumn.propTypes = {
    capacity: T.number
  };

  const renderTypeColumn = ({ source_type }) => {
    const binTypeTheme = {
      fas: 'brand-blue',
      manual: 'brand-gray'
    };

    return (
      <CustomColumn flexRow label={labels.dataTable.type}>
        <StatusBadge
          status={source_type}
          theme={binTypeTheme}
        />
      </CustomColumn>
    );
  };

  renderTypeColumn.propTypes = {
    source_type: T.string
  };

  const getDropdownData = (bin) => ([{
    label: <FormattedMessage id="general.button.delete" />,
    onClick: () => openDeleteModal(bin),
    hide: !bin.editable
  }]);

  const renderActionsColumn = (bin, { rowIndex }) => {
    const canEdit = bin.editable;

    return (
      <div className="collapsible-value button-column">
        <DropdownButton
          idKey={`${bin.id}-${rowIndex}`}
          wide
          buttonType={canEdit ? 'small light-gray' : 'small light-gray disabled'}
          label={<FormattedMessage id="general.button.edit" />}
          onClick={canEdit ? () => openEditModal(bin) : () => {}}
          dropDownData={getDropdownData(bin)}
          customClass="show-for-large"
        />
      </div>
    );
  };

  const columns = [
    {
      label: labels.dataTable.name,
      flex: '1 0 200px',
      renderer: renderBinNameColumn,
      sortKey: 'name'
    },
    {
      label: labels.dataTable.capacity,
      flex: '1 0 150px',
      renderer: renderCapacityColumn,
      sortKey: 'capacity'
    },
    {
      label: labels.dataTable.type,
      flex: '1 0 120px',
      renderer: renderTypeColumn,
      sortKey: 'source_type'
    },
    {
      label: '',
      flex: '0 0 135px',
      renderer: renderActionsColumn,
      fixed: true
    },
  ];

  // Show FAS notice if integration is active
  const renderFasNotice = () => {
    if (fasStatus.loading) return null;

    if (fasStatus.has_fas_integration) {
      return (
        <MessageBox type="info" className="mb-15">
          <FormattedMessage id="component.farmBins.fasIntegrationActive" />
        </MessageBox>
      );
    }
    return null;
  };

  const importLink = `/csv-import/select-mode?from=bins&entityId=${farmId}`;

  return (
    <>
      <Panel className="FarmBins">
        <Panel.Heading title={labels.bins}>
          <div className="panel-heading-actions">
            <SearchBox initialValue={search} onChange={onSearchChange} />
            <Link className="importTableBtn" to={importLink}>
              <FormattedMessage id="general.import" />
            </Link>
            <button
              className="button small primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openAddModal();
              }}
              disabled={isUpdating}
            >
              <i className="fa fa-plus mr-5" />
              {labels.addBin}
            </button>
          </div>
        </Panel.Heading>

        <Panel.Body noPadding>
          {renderFasNotice()}

          <DataTable
            data={resources}
            columns={columns}
            sort={sort}
            onSortChange={onSortChange}
            isLoading={isLoading}
            paginationProps={paginationProps}
          />

          <NothingBox
            display={!isLoading && resources.length === 0}
          >
            <FormattedMessage tagName="h1" id="component.nothingBox.farmHasNoBins" />
            <EmptyStateActions
              importTo={importLink}
              primaryLabel={labels.addBin}
              onPrimaryClick={openAddModal}
              disabled={isUpdating}
            />
          </NothingBox>
        </Panel.Body>
      </Panel>
    </>
  );
};

FarmBins.propTypes = {
  resources: T.array.isRequired,
  fetchData: T.func.isRequired,
  isLoading: T.bool.isRequired,
  reqParams: T.object.isRequired,
  params: T.object,
  meta: T.object,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  openModal: T.func.isRequired,
};

export default connect(
  (state) => ({
    resources: state.dataTable.farmBins.resources,
    reqParams: state.dataTable.farmBins.params,
    isLoading: state.dataTable.farmBins.isLoading,
    meta: state.dataTable.farmBins.meta,
    measurementSystem: state.auth.user.current_company.measurement_system,
  }), (dispatch, { params: { id } }) => ({
    fetchData: (query) => dispatch(fetchData(`/admin/farms/${id}/bins`, 'farmBins', query)),
    openModal: (modal) => dispatch(openModal(modal)),
  }),
)(FarmBins);
